// src/hooks/useRealtimeSubscription.js
import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Custom hook for Supabase realtime subscriptions
 *
 * @param {string} channelName - Unique name for this subscription channel
 * @param {string} table - Database table to subscribe to
 * @param {Object} options - Configuration options
 * @param {Function} options.onInsert - Callback when a row is inserted
 * @param {Function} options.onUpdate - Callback when a row is updated
 * @param {Function} options.onDelete - Callback when a row is deleted
 * @param {string} options.filter - Optional filter (e.g., 'user_id=eq.123')
 * @param {boolean} options.enabled - Whether subscription is active (default: true)
 *
 * @example
 * useRealtimeSubscription('proposals', 'proposals', {
 *   onInsert: (newOffer) => setOffers(prev => [newOffer, ...prev]),
 *   onUpdate: (updatedOffer) => setOffers(prev =>
 *     prev.map(o => o.id === updatedOffer.id ? updatedOffer : o)
 *   ),
 *   filter: `customer_id=eq.${userId}`
 * });
 */
export function useRealtimeSubscription(channelName, table, options = {}) {
  const {
    onInsert,
    onUpdate,
    onDelete,
    filter,
    enabled = true
  } = options;

  const channelRef = useRef(null);
  const onInsertRef = useRef(onInsert);
  const onUpdateRef = useRef(onUpdate);
  const onDeleteRef = useRef(onDelete);

  // Keep callback refs current without re-triggering the subscription
  onInsertRef.current = onInsert;
  onUpdateRef.current = onUpdate;
  onDeleteRef.current = onDelete;

  useEffect(() => {
    if (!supabase || !enabled) return;

    // Create channel
    const channel = supabase.channel(channelName);

    // Build filter config
    const config = {
      event: '*',
      schema: 'public',
      table: table
    };

    if (filter) {
      config.filter = filter;
    }

    // Subscribe to postgres changes
    channel
      .on('postgres_changes', config, (payload) => {
        if (import.meta.env.VITE_DEBUG) {
          console.log(`[Realtime ${table}]`, payload.eventType, payload.new || payload.old);
        }

        switch (payload.eventType) {
          case 'INSERT':
            onInsertRef.current?.(payload.new);
            break;
          case 'UPDATE':
            onUpdateRef.current?.(payload.new, payload.old);
            break;
          case 'DELETE':
            onDeleteRef.current?.(payload.old);
            break;
          default:
            break;
        }
      })
      .subscribe((status) => {
        if (import.meta.env.VITE_DEBUG) {
          console.log(`[Realtime ${channelName}] Subscription status:`, status);
        }
      });

    channelRef.current = channel;

    // Cleanup on unmount or when channel identity changes
    return () => {
      if (channelRef.current) {
        if (import.meta.env.VITE_DEBUG) {
          console.log(`[Realtime ${channelName}] Unsubscribing...`);
        }
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [channelName, table, filter, enabled]);

  return channelRef.current;
}
